'use client';

import * as React from 'react';
import Image from 'next/image';

import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  type CardProps
} from '@workspace/ui/components/card';
import { cn } from '@workspace/ui/lib/utils';

const CARTA_EJEMPLO_IMAGE =
  'https://www.figma.com/api/mcp/asset/f0b5098f-e31f-4a35-afc3-a4ae7497dc03';

const SAVINGS_OPTIONS = [
  { value: 1000, label: '$1000' },
  { value: 6000, label: '$6000' },
  { value: 10000, label: '$10000' }
] as const;

interface SavingsCardProps extends Omit<CardProps, 'onSelect'> {
  /**
   * Callback fired when a savings amount is selected
   */
  onSelect?: (amount: number) => void;
  /**
   * Currently selected amount
   */
  selectedAmount?: number;
  /**
   * Current step in the flow (e.g., "1/6")
   */
  step?: string;
  /**
   * Whether to show the interactive buttons or just the image
   */
  interactive?: boolean;
}

export function SavingsCard({
  className,
  onSelect,
  selectedAmount,
  step = '1/6',
  interactive = true,
  ...props
}: SavingsCardProps): React.JSX.Element {
  const [selected, setSelected] = React.useState<number | undefined>(
    selectedAmount
  );

  const handleSelect = (amount: number) => {
    setSelected(amount);
    onSelect?.(amount);
  };

  if (!interactive) {
    return (
      <Card
        className={cn('overflow-hidden p-0', className)}
        {...props}
      >
        <div className="relative aspect-[384/552]">
          <Image
            src={CARTA_EJEMPLO_IMAGE}
            alt="¿Cuanto puedes ahorrar por mes?"
            fill
            className="object-cover"
            priority
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'relative overflow-hidden bg-gradient-to-b from-[#2d4a7c] via-[#3d6b8c] to-[#6fac8b] p-0',
        className
      )}
      {...props}
    >
      <CardContent className="space-y-6 p-6">
        {/* Question Section */}
        <div className="relative">
          <div className="flex items-start gap-4">
            {/* Character placeholder - would need actual character image */}
            <div className="relative size-24 shrink-0">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-400 to-purple-600" />
              {/* Character would go here */}
            </div>
            <div className="flex-1">
              <div className="rounded-2xl rounded-tl-none bg-white px-4 py-3 shadow-lg">
                <h2 className="text-lg font-bold text-[#1a3a5c] sm:text-xl">
                  ¿Cuanto puedes ahorrar por mes?
                </h2>
              </div>
              <div className="mt-2 flex justify-end">
                <div className="flex size-8 items-center justify-center rounded-full bg-cyan-400 text-sm font-bold text-white shadow-md">
                  ?
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Amount Options */}
        <div className="space-y-3 px-2">
          {SAVINGS_OPTIONS.map((option) => (
            <Button
              key={option.value}
              onClick={() => handleSelect(option.value)}
              variant={selected === option.value ? 'default' : 'secondary'}
              size="lg"
              className={cn(
                'h-14 w-full rounded-3xl text-2xl font-bold transition-all',
                selected === option.value
                  ? 'bg-gradient-to-r from-green-300 to-green-200 text-[#1a3a5c] shadow-lg ring-2 ring-green-400'
                  : 'bg-gradient-to-r from-green-200/80 to-green-100/80 text-[#1a3a5c] hover:from-green-300/90 hover:to-green-200/90'
              )}
            >
              {option.label}
            </Button>
          ))}
        </div>

        {/* Decorative dollar signs */}
        <div className="pointer-events-none absolute left-4 top-20 text-4xl font-bold text-white/20">
          $
        </div>
        <div className="pointer-events-none absolute right-4 top-20 text-4xl font-bold text-white/20">
          $
        </div>
        <div className="pointer-events-none absolute bottom-32 left-4 text-4xl font-bold text-white/20">
          $
        </div>
        <div className="pointer-events-none absolute bottom-32 right-4 text-4xl font-bold text-white/20">
          $
        </div>
      </CardContent>

      <CardFooter className="flex items-center justify-between rounded-b-3xl bg-[#1a3a5c] px-6 py-4">
        <div className="flex items-center gap-2">
          <div className="rounded-full border-2 border-white px-4 py-1">
            <span className="text-sm font-semibold text-white">
              AutofinPlan
            </span>
          </div>
        </div>
        <div className="text-sm font-semibold text-white">{step}</div>
      </CardFooter>
    </Card>
  );
}
